import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Button from "Components/Button.js";
import Card from "Components/Card.js";
import Loader from "Components/Loader.js";
import TextField from "Components/TextField.js";
import RaildaysService from "Services/RaildaysService.js";

const ERROR_COULD_NOT_LOCATE = "We're sorry, we could not locate those details. Please try again.";
const ERROR_VISIT_DATE_EXPIRED = "Sorry, you can no longer post feedback for this visit date.";
const FEEDBACK_SCORES = [1, 2, 3, 4, 5];

const formatVisitDate = value => {
	if (!value) return "";
	const [year, month, day] = value.split("-");
	if (!year || !month || !day) return value;
	return `${day}/${month}/${year}`;
};

const getFeedbackExistsMessage = visitDate => (
	`Feedback can only be given once per visit, and we have already recorded your feedback from your visit on ${formatVisitDate(visitDate)}.`
);

const buildCreatePayload = ({orderNumber, postcode, visitDate}) => ({
	orderNumber: (orderNumber || "").trim(),
	postcode: (postcode || "").trim(),
	visitDate: (visitDate || "").trim()
});

const buildSalesOrderPayload = ({salesOrderUuid, visitDate}) => ({
	salesOrderUuid: (salesOrderUuid || "").trim(),
	visitDate: (visitDate || "").trim()
});

const extractFeedbackId = value => {
	if (value === undefined || value === null) return "";
	if ((typeof value === "string") || (typeof value === "number")) {
		return String(value).trim().replace(/^['"]+|['"]+$/g, "");
	}
	if (typeof value !== "object") return "";
	const nestedValue = value?.data || value?.result || value?.payload;
	if (nestedValue && nestedValue !== value) {
		const nestedId = extractFeedbackId(nestedValue);
		if (nestedId) return nestedId;
	}
	return String(
		value?.feedbackId ||
		value?.feedbackID ||
		value?.ticketFeedbackId ||
		value?.ticketFeedbackID ||
		value?.id ||
		value?.Id ||
		""
	).trim().replace(/^['"]+|['"]+$/g, "");
};

const extractFeedbackIdFromUnknownResponse = value => {
	const directId = extractFeedbackId(value);
	if (directId) return directId;
	const asText = (value === undefined || value === null) ? "" : JSON.stringify(value);
	const match = asText.match(/"?(\d{1,12})"?/);
	return match ? match[1] : "";
};

const RaildaysFeedbackView = () => {
	const navigate = useNavigate();
	const {salesOrderUuid, visitDate: visitDateFromRoute} = useParams();

	const [orderNumber, setOrderNumber] = useState("");
	const [postcode, setPostcode] = useState("");
	const [visitDate, setVisitDate] = useState("");
	const [error, setError] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [hideForm, setHideForm] = useState(false);
	const autoSubmitAttemptedRef = useRef(false);

	const hasPrefilledRoute = useMemo(
		() => Boolean((salesOrderUuid || "").trim() && (visitDateFromRoute || "").trim()),
		[salesOrderUuid, visitDateFromRoute]
	);

	const submitByOrderDetails = useCallback(async payload => {
		const response = await RaildaysService.createFeedback(payload);
		return response;
	}, []);

	const submitBySalesOrderUuid = useCallback(async payload => {
		const response = await RaildaysService.createFeedbackBySalesOrderUuid(payload);
		return response;
	}, []);

	const processCreateResponse = useCallback((status, data, currentVisitDate) => {
		const feedbackId = extractFeedbackIdFromUnknownResponse(data);
		if ((status === 200 || status === 201) && feedbackId) {
			navigate(`/feedback-questions/${feedbackId}`);
			return;
		}
		if (status === 409) {
			const message = getFeedbackExistsMessage(currentVisitDate);
			if (hasPrefilledRoute) {
				setError(message);
				setHideForm(true);
			}
			else {
				setError(`${message} If you have visited us again please enter the date and order number of your new visit.`);
			}
			return;
		}
		if (status === 412) {
			setError(ERROR_VISIT_DATE_EXPIRED);
			if (hasPrefilledRoute) setHideForm(true);
			return;
		}
		setError(ERROR_COULD_NOT_LOCATE);
		if (hasPrefilledRoute) setHideForm(true);
	}, [hasPrefilledRoute, navigate]);

	useEffect(() => {
		if (!hasPrefilledRoute) return undefined;
		if (autoSubmitAttemptedRef.current) return undefined;
		autoSubmitAttemptedRef.current = true;

		let isMounted = true;
		const run = async () => {
			setSubmitting(true);
			setError("");
			try {
				const payload = buildSalesOrderPayload({
					salesOrderUuid,
					visitDate: visitDateFromRoute
				});
				const {status, data} = await submitBySalesOrderUuid(payload);
				if (!isMounted) return;
				processCreateResponse(status, data, payload.visitDate);
			}
			catch (requestError) {
				if (isMounted) {
					setError(ERROR_COULD_NOT_LOCATE);
					setHideForm(true);
				}
			}
			finally {
				if (isMounted) setSubmitting(false);
			}
		};

		run();
		return () => {
			isMounted = false;
		};
	}, [
		hasPrefilledRoute,
		processCreateResponse,
		salesOrderUuid,
		submitBySalesOrderUuid,
		visitDateFromRoute
	]);

	const handleSubmit = useCallback(async event => {
		event.preventDefault();
		setSubmitting(true);
		setError("");

		try {
			if (hasPrefilledRoute) {
				const payload = buildSalesOrderPayload({
					salesOrderUuid,
					visitDate: visitDateFromRoute
				});
				const {status, data} = await submitBySalesOrderUuid(payload);
				processCreateResponse(status, data, payload.visitDate);
			}
			else {
				const payload = buildCreatePayload({orderNumber, postcode, visitDate});
				if (!payload.orderNumber || !payload.postcode || !payload.visitDate) {
					setError(ERROR_COULD_NOT_LOCATE);
					setSubmitting(false);
					return;
				}
				const {status, data} = await submitByOrderDetails(payload);
				processCreateResponse(status, data, payload.visitDate);
			}
		}
		catch (requestError) {
			setError(ERROR_COULD_NOT_LOCATE);
		}
		finally {
			setSubmitting(false);
		}
	}, [
		hasPrefilledRoute,
		orderNumber,
		postcode,
		processCreateResponse,
		salesOrderUuid,
		submitByOrderDetails,
		submitBySalesOrderUuid,
		visitDate,
		visitDateFromRoute
	]);

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				mx: {xs: 2, md: 3, lg: 5},
				mb: {xs: 2, md: 3, lg: 5},
				backgroundColor: "#fff",
				borderRadius: 2
			}}>
			<Container maxWidth={false} sx={{py: {xs: 4, md: 6}, px: {xs: 2, md: 4, lg: 6}}}>
				<Breadcrumbs sx={{textTransform: "uppercase", color: "#e2b534"}}>
					<Button label="Home" uri="/" style={{color: "#e2b534"}} variant="text" />
					<Typography color="#e2b534">Feedback</Typography>
				</Breadcrumbs>

				<Typography variant="h4" sx={{mt: 2, fontWeight: 800, textTransform: "uppercase"}}>
					Feedback
				</Typography>

				<Box sx={{maxWidth: 760, mx: "auto", mt: 4}}>
					{error && (
						<Alert severity="error" sx={{mb: 3}}>
							<Typography>{error}</Typography>
						</Alert>
					)}

					{(hasPrefilledRoute && submitting) && (
						<Stack alignItems="center" sx={{py: 4}}>
							<Loader />
						</Stack>
					)}

					{(!hideForm && !hasPrefilledRoute) && (
						<Card px={0} py={0} style={{borderRadius: 12, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)", overflow: "hidden"}}>
							<Box
								sx={{
									p: {xs: 3, md: 4},
									background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)"
								}}
							>
								<Typography sx={{fontWeight: 800, color: "#111827"}} variant="h6">
									Tell us about your visit
								</Typography>
								<Typography sx={{mt: 1, color: "#475569"}}>
									Thank you for visiting. Enter your order details to continue to the feedback questions.
								</Typography>
								<Stack direction="row" spacing={1.25} sx={{mt: 2}}>
									{FEEDBACK_SCORES.map(score => (
										<Box
											key={`score-preview-${score}`}
											component="img"
											alt={`Feedback score ${score}`}
											src={`/assets/Raildays/feedback/smiley_${score}.svg`}
											sx={{width: 28, height: 28, opacity: 0.9}}
										/>
									))}
								</Stack>
							</Box>

							<Divider />

							<Box sx={{p: {xs: 3, md: 4}}}>
								<form onSubmit={handleSubmit}>
									<Grid container spacing={2}>
										<Grid item xs={12} md={6}>
											<TextField
												controlled={true}
												fullWidth={true}
												helperText="Example: L123456"
												label="Order Number"
												onChange={setOrderNumber}
												placeholder="L123456"
												required={true}
												size="small"
												value={orderNumber}
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<TextField
												controlled={true}
												fullWidth={true}
												helperText="Use the booking postcode"
												label="Postcode"
												onChange={setPostcode}
												placeholder="ABC DEF"
												required={true}
												size="small"
												value={postcode}
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<TextField
												controlled={true}
												fullWidth={true}
												helperText="Choose the date you visited"
												inputClassname={visitDate ? "has-value" : undefined}
												labelShrink={true}
												label="Visit Date"
												onChange={setVisitDate}
												required={true}
												size="small"
												type="date"
												value={visitDate}
											/>
										</Grid>
									</Grid>

									<Box sx={{display: "flex", justifyContent: {xs: "stretch", md: "flex-end"}, mt: 3}}>
										{submitting ?
											(
												<Loader size={20} />
											) :
											(
												<Button label="Continue to Questions" type="submit" variant="outlined" />
											)}
									</Box>
								</form>
							</Box>
						</Card>
					)}
				</Box>
			</Container>
		</Box>
	);
};

export default RaildaysFeedbackView;
